import { Link } from "react-router-dom";
import { Lock, Crown, DiamondsFour, Check, X } from "@phosphor-icons/react";
import { format, isToday, isTomorrow } from "date-fns";
import { es } from "date-fns/locale";
import TeamLogo from "./betiq/TeamLogo";
import LeagueBadge, { SPORT_ACCENT } from "./LeagueBadge";
import { resolveTeamLogo } from "../lib/resolveTeamLogo";
import { useLiveEvents, findLiveMatch } from "../hooks/useLiveEvents";

/* ── Market labels in plain Spanish ─────────────────────────── */
const MARKET_LABELS = {
  "1x2":            "Resultado Final",
  "over_under":     "Más / Menos",
  "moneyline":      "Ganador Directo",
  "spread":         "Hándicap de Puntos",
  "run_line":       "Spread de Béisbol",
  "btts":           "Ambos Anotan",
  "double_chance":  "Doble Oportunidad",
  "asian_handicap": "Hándicap Asiático",
  "total_points":   "Total de Puntos",
  "handicap":       "Hándicap",
  "draw_no_bet":    "Sin Empate",
};

function marketLabel(raw) {
  if (!raw) return "";
  const key = raw.toLowerCase().replace(/[\s-]/g, "_");
  return MARKET_LABELS[key] || raw;
}

/* ── One-line plain-language explanation ─────────────────────── */
function buildMiniExplain(market, sel, home, away, sport) {
  const mkt = (market || "").toLowerCase();
  const s   = (sel    || "").trim();

  const overM  = /(?:m[aá]s de|over)\s*([\d.]+)/i.exec(s);
  const underM = /(?:menos de|under)\s*([\d.]+)/i.exec(s);
  const line   = overM?.[1] || underM?.[1];
  const isOver = !!overM;

  const unit = sport === "mlb" ? "carreras" : sport === "nba" ? "puntos" : "goles";

  // Over / Under
  if ((isOver || underM) && line) {
    const n = parseFloat(line);
    return isOver
      ? `Los dos equipos suman ${Math.ceil(n)} ${unit} o más`
      : `Los dos equipos suman ${Math.floor(n)} ${unit} o menos`;
  }

  // 1X2
  if (mkt.includes("1x2") || mkt.includes("resultado")) {
    const map = { "1": `Gana ${home}`, "X": "Termina en empate", "2": `Gana ${away}` };
    if (map[s]) return map[s];
    // If selection is a team name instead of 1/X/2
    const sl = s.toLowerCase();
    if (sl && home && sl.includes(home.toLowerCase().split(" ")[0])) return `Gana ${home}`;
    if (sl && away && sl.includes(away.toLowerCase().split(" ")[0])) return `Gana ${away}`;
    return `Gana quien seleccionó Ronald`;
  }

  // Moneyline
  if (mkt.includes("moneyline") || mkt.includes("ganador")) {
    const team = s.replace(/\s*ml\s*/i, "").trim() || home;
    return `${team} gana el partido por cualquier marcador`;
  }

  // BTTS
  if (mkt.includes("btts") || mkt.includes("ambos")) {
    const yes = /s[ií]/i.test(s) || s === "";
    return yes
      ? `Ambos equipos deben anotar al menos 1 gol`
      : `Al menos uno de los equipos no anota`;
  }

  // Spread / Handicap
  if (mkt.includes("spread") || mkt.includes("handicap") || mkt.includes("hándicap") || mkt.includes("run_line")) {
    const hm = /([+-][\d.]+)/.exec(s);
    const hv = hm?.[1];
    if (hv) {
      const isFav = hv.startsWith("-");
      const abs = hv.replace(/[+-]/, "");
      const team = s.split(/[+-]/)[0].trim() || home;
      return isFav
        ? `${team} debe ganar por más de ${abs} puntos`
        : `${team} puede perder hasta ${abs} puntos y la jugada gana`;
    }
  }

  // Double chance
  if (mkt.includes("doble") || mkt.includes("double")) {
    return `Cubre dos resultados posibles — más seguro, menor cuota`;
  }

  return null;
}

/* ── Live badge builder ──────────────────────────────────────── */
function buildLiveBadge(ev, sport) {
  const desc   = (ev.status_desc  || "").toLowerCase();
  const clock  = ev.display_clock || "";
  const period = ev.period        || 0;

  // Extra innings MLB
  if (desc.includes("extra") || desc.includes("extra innings")) {
    return { label: `EXTRA INNING · ${period}`, cls: "bg-orange-500/20 text-orange-300 border-orange-400/40", pulse: true };
  }
  // Overtime NBA/NHL
  if (desc.includes("overtime") || desc.includes("ot")) {
    return { label: `TIEMPO EXTRA · ${clock}`, cls: "bg-purple-500/20 text-purple-300 border-purple-400/40", pulse: true };
  }
  // Football (soccer)
  if (sport === "football") {
    const minLabel = clock ? `${clock}'` : (period === 2 ? "2T" : "1T");
    return { label: `EN JUEGO · ${minLabel}`, cls: "bg-blue-500/15 text-blue-300 border-blue-400/30", pulse: true };
  }
  // NBA quarters
  if (sport === "nba") {
    const qLabel = period ? `Q${period}` : "";
    return { label: `EN JUEGO · ${qLabel}${clock ? ` · ${clock}` : ""}`, cls: "bg-blue-500/15 text-blue-300 border-blue-400/30", pulse: true };
  }
  // MLB innings
  if (sport === "mlb") {
    const inningLabel = period ? `${period}ª entrada` : "";
    return { label: `EN JUEGO · ${inningLabel}`, cls: "bg-blue-500/15 text-blue-300 border-blue-400/30", pulse: true };
  }
  // Default
  return { label: "EN JUEGO", cls: "bg-blue-500/15 text-blue-300 border-blue-400/30", pulse: true };
}

/* ── "Empieza" badge when match hasn't started ───────────────── */
function buildPendingBadge(matchDate) {
  if (!matchDate) return { label: "PENDIENTE", cls: "bg-[#F59E0B]/10 text-[#F59E0B] border-[#F59E0B]/20", pulse: false };
  const now = new Date();
  const diff = matchDate - now;

  if (diff > 0) {
    const timeStr = format(matchDate, "HH:mm");
    if (isToday(matchDate))    return { label: `Hoy · ${timeStr}`,    cls: "bg-[#F59E0B]/10 text-[#F59E0B] border-[#F59E0B]/20", pulse: false };
    if (isTomorrow(matchDate)) return { label: `Mañana · ${timeStr}`, cls: "bg-[#F59E0B]/10 text-[#F59E0B] border-[#F59E0B]/20", pulse: false };
    return { label: format(matchDate, "dd MMM · HH:mm", { locale: es }), cls: "bg-[#F59E0B]/10 text-[#F59E0B] border-[#F59E0B]/20", pulse: false };
  }

  // Match time has passed but no admin update — time-based fallback
  return { label: "EN JUEGO", cls: "bg-blue-500/15 text-blue-300 border-blue-400/30", pulse: true };
}

/* ── Status styles for resolved picks ───────────────────────── */
const STATUS_STYLE = {
  won:  { label: "GANADA",   cls: "bg-[#10B981]/10 text-[#10B981] border-[#10B981]/20", pulse: false },
  lost: { label: "PERDIDA",  cls: "bg-[#F43F5E]/10 text-[#F43F5E] border-[#F43F5E]/20", pulse: false },
  void: { label: "ANULADA",  cls: "bg-white/5 text-zinc-400 border-white/10",            pulse: false },
};

/* ─────────────────────────────────────────────────────────────── */
export default function PickCard({ pick, compact = false }) {
  const liveEvents = useLiveEvents();

  const matchDate = pick.match_date ? new Date(pick.match_date) : null;
  const isLocked  = pick.locked;
  const conf      = pick.confidence || 0;
  const isPremium = conf >= 7;
  const isWon     = pick.status === "won";

  const confColor = conf >= 7 ? "#10B981" : conf >= 5 ? "#F59E0B" : "#D4AF37";
  const confLabel = conf >= 8 ? "MUY ALTA" : conf >= 6 ? "ALTA" : conf >= 4 ? "MEDIA" : "MODERADA";

  const homeLogo = pick.home_logo || resolveTeamLogo(pick.sport, pick.home_team);
  const awayLogo = pick.away_logo || resolveTeamLogo(pick.sport, pick.away_team);
  const accent   = SPORT_ACCENT[pick.sport] || SPORT_ACCENT.football;

  const surfaceClass = isWon ? "surface-won" : isPremium ? "surface-premium" : "surface";
  // Pick VIP highlight: pulse + golden border when the pick is for sale and user hasn't purchased
  const isPickVipForSale = pick.is_paid_pick && pick.pick_price && isLocked;

  // Sport ambient colors for subtle background
  const sportGlow = {
    football: "rgba(34,197,94,",
    nba:      "rgba(251,146,60,",
    mlb:      "rgba(248,113,113,",
    tennis:   "rgba(250,204,21,",
  }[pick.sport] || "rgba(212,175,55,";

  // ── Determine status badge ──────────────────────────────────
  let statusBadge;
  if (pick.status === "won" || pick.status === "lost" || pick.status === "void") {
    statusBadge = STATUS_STYLE[pick.status];
  } else {
    // pending or live — check ESPN first
    const liveMatch = findLiveMatch(liveEvents, pick.home_team, pick.away_team);
    if (liveMatch) {
      statusBadge = buildLiveBadge(liveMatch, pick.sport);
    } else if (pick.status === "live") {
      // Admin manually set to live
      statusBadge = { label: "EN JUEGO", cls: "bg-blue-500/15 text-blue-300 border-blue-400/30", pulse: true };
    } else {
      statusBadge = buildPendingBadge(matchDate);
    }
  }

  // ── Live score (only when matched to ESPN) ────────────────
  const liveMatch = pick.status !== "won" && pick.status !== "lost" && pick.status !== "void"
    ? findLiveMatch(liveEvents, pick.home_team, pick.away_team)
    : null;

  // ── Mini-explanation ──────────────────────────────────────
  const miniExplain = buildMiniExplain(pick.market, pick.pick_selection, pick.home_team, pick.away_team, pick.sport);

  return (
    <Link
      to={`/jugadas/${pick.id}`}
      data-testid={`pick-card-${pick.id}`}
      className={`group block relative overflow-hidden transition-all duration-300 hover:-translate-y-1.5 ${surfaceClass} rounded-2xl ${isPickVipForSale ? "pickvip-glow" : ""}`}
      style={isPickVipForSale ? {
        border: "1.5px solid rgba(212,175,55,0.55)",
        boxShadow: "0 0 0 1px rgba(212,175,55,0.15), 0 8px 32px -8px rgba(212,175,55,0.4)",
      } : undefined}
    >
      {/* Sport ambient glow — very subtle bottom-left radial */}
      <div style={{
        position: "absolute", bottom: 0, left: 0,
        width: 120, height: 120,
        background: `radial-gradient(circle, ${sportGlow}0.06) 0%, transparent 70%)`,
        pointerEvents: "none", zIndex: 0, borderRadius: "0 0 0 16px",
      }} />
      {isPickVipForSale && (
        <>
          <div className="absolute inset-x-0 top-0 h-px overflow-hidden z-10">
            <div className="h-full pickvip-shimmer" style={{
              background: "linear-gradient(90deg, transparent, #D4AF37, transparent)",
            }} />
          </div>
        </>
      )}
      {/* Sport-color left accent bar */}
      <div style={{
        position: "absolute", top: 0, left: 0, bottom: 0, width: 3,
        background: `linear-gradient(180deg, ${accent.color}, transparent)`,
        borderRadius: "0 0 0 16px",
        opacity: isWon ? 0 : 0.85,
      }} />

      {/* Lock overlay */}
      {isLocked && (
        <div className="absolute inset-0 backdrop-blur-sm bg-black/50 z-10 flex items-center justify-center">
          <div className="flex flex-col items-center gap-2 px-5 py-4 rounded-xl"
            style={{ background: "#141417", border: "1px solid rgba(212,175,55,0.3)" }}>
            <Lock className="w-6 h-6" weight="fill" style={{ color: "#D4AF37" }} />
            <div className="text-[10px] tracking-widest uppercase font-black" style={{ color: "#D4AF37" }}>Premium</div>
          </div>
        </div>
      )}

      <div className="p-5 pl-6">
        {/* Header: Liga + status badge */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <LeagueBadge sport={pick.sport} league={pick.league} size="sm" />
            <span className="text-xs uppercase tracking-wider font-semibold" style={{ color: "#B0B0C8" }}>
              {pick.league}
            </span>
            {pick.access_tier === "vip" && (
              <Crown weight="fill" className="w-3 h-3" style={{ color: "#D4AF37" }} />
            )}
            {pick.is_paid_pick && pick.pick_price && (
              <span
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-widest"
                style={{
                  background: "rgba(212,175,55,0.15)",
                  color: "#D4AF37",
                  border: "1px solid rgba(212,175,55,0.4)",
                }}
                data-testid={`pick-vip-badge-${pick.id}`}
              >
                💎 ${Number(pick.pick_price).toFixed(0)}
              </span>
            )}
          </div>

          <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide border ${statusBadge.cls}`}
            data-testid={`pick-status-badge-${pick.id}`}>
            {statusBadge.pulse && (
              <span className="relative flex h-1.5 w-1.5 flex-shrink-0">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-current opacity-75" />
                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-current" />
              </span>
            )}
            {statusBadge.label}
          </span>
        </div>

        {/* Teams + live score */}
        <div className="mb-4 space-y-2.5">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2.5 min-w-0">
              <TeamLogo logoUrl={homeLogo} teamName={pick.home_team} size={28} rounded="md" />
              <div className="font-outfit font-semibold text-base leading-tight text-white truncate">
                {pick.home_team}
              </div>
            </div>
            {liveMatch && liveMatch.home_score != null && (
              <span className="font-black text-lg text-white font-mono flex-shrink-0" data-testid={`live-score-home-${pick.id}`}>
                {liveMatch.home_score}
              </span>
            )}
          </div>

          <div className="text-[10px] font-mono ml-9 uppercase tracking-widest font-bold" style={{ color: "#3F3F46" }}>
            vs
          </div>

          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2.5 min-w-0">
              <TeamLogo logoUrl={awayLogo} teamName={pick.away_team} size={28} rounded="md" />
              <div className="font-outfit font-semibold text-base leading-tight text-zinc-300 truncate">
                {pick.away_team}
              </div>
            </div>
            {liveMatch && liveMatch.away_score != null && (
              <span className="font-black text-lg text-zinc-300 font-mono flex-shrink-0" data-testid={`live-score-away-${pick.id}`}>
                {liveMatch.away_score}
              </span>
            )}
          </div>

          {matchDate && !liveMatch && (
            <div className="text-xs font-manrope ml-9" style={{ color: "#B0B0C8" }}>
              {format(matchDate, "dd MMM · HH:mm")}
            </div>
          )}
        </div>

        {/* Pick selection + market label + mini-explanation */}
        <div className="pt-3 mb-3" style={{ borderTop: "1px solid rgba(255,255,255,0.05)" }}>
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold mb-1" style={{ color: "#9898B8" }}>
                {marketLabel(pick.market)}
              </div>
              <div className="text-sm font-manrope font-semibold text-white">{pick.pick_selection}</div>
              {miniExplain && (
                <div className="mt-1 text-[11px] leading-relaxed" style={{ color: "#B0B0C8" }}
                  data-testid={`pick-mini-explain-${pick.id}`}>
                  {miniExplain}
                </div>
              )}
            </div>
            {/* Odds badge */}
            {pick.odds && (
              <div className="flex-shrink-0 px-2.5 py-1.5 rounded-lg text-center"
                style={{
                  background: "rgba(212,175,55,0.08)",
                  border: "1px solid rgba(212,175,55,0.25)",
                  boxShadow: "0 0 10px rgba(212,175,55,0.1)",
                }}>
                <div className="text-[9px] font-bold uppercase tracking-wider" style={{ color: "#9898B8" }}>cuota</div>
                <div className="font-black text-sm" style={{
                  fontFamily: "'JetBrains Mono', monospace",
                  color: "#D4AF37",
                  filter: "drop-shadow(0 0 4px rgba(212,175,55,0.5))",
                }}>
                  {Number(pick.odds).toFixed(2)}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Notas exclusivas de Ronald — visibles para todos los suscriptores */}
        {!isLocked && pick.stats?.notes && (
          <div className="pickcard-notes" data-testid={`pick-notes-${pick.id}`}>
            <div className="pickcard-notes-label">
              <span className="pickcard-notes-dot" />
              NOTA DE RONALD
            </div>
            <div className="pickcard-notes-text">{pick.stats.notes}</div>
          </div>
        )}

        {/* Confianza de Ronald */}
        {!compact && conf > 0 && (
          <div className="pt-3" style={{ borderTop: "1px solid rgba(255,255,255,0.05)" }}>
            <div className="flex items-center justify-between mb-1.5">
              <div className="flex items-center gap-1.5">
                <DiamondsFour weight="fill" className="w-3 h-3" style={{ color: "#D4AF37" }} />
                <span className="text-[10px] uppercase tracking-[0.2em] font-bold" style={{ color: "#B0B0C8" }}>
                  Confianza Ronald
                </span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-[10px] font-bold uppercase" style={{ color: confColor }}>{confLabel}</span>
                <span className="font-outfit font-black text-sm" style={{ color: confColor, filter: `drop-shadow(0 0 6px ${confColor}99)` }}>{conf}/10</span>
              </div>
            </div>
            <div className="h-1.5 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.06)" }}>
              <div className="h-full rounded-full transition-all duration-700 relative"
                style={{
                  width: `${conf * 10}%`,
                  background: `linear-gradient(90deg, ${confColor}88, ${confColor})`,
                  boxShadow: `0 0 8px ${confColor}66`,
                }} />
            </div>
          </div>
        )}

        {/* Resultado animado */}
        {pick.status !== "pending" && pick.status !== "live" && (
          <div className="mt-3 pt-3 pick-result-wrap"
            style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}>

            {pick.status === "won" ? (
              <div className="prs-won-block" data-testid="result-badge-won">
                <span className="prs-status-label prs-status-won">
                  <Check weight="bold" className="prs-check-icon" /> GANADA
                </span>
                {pick.result_note && (
                  <p className="prs-result-text prs-result-text--won">{pick.result_note}</p>
                )}
              </div>
            ) : pick.status === "lost" ? (
              <div className="prs-won-block" data-testid="result-badge-lost">
                <span className="prs-status-label prs-status-lost">
                  <X weight="bold" className="prs-check-icon" /> PERDIDA
                </span>
                {pick.result_note && (
                  <p className="prs-result-text prs-result-text--lost">{pick.result_note}</p>
                )}
              </div>
            ) : (
              <div className="prs-won-block" data-testid="result-badge-void">
                <span className="prs-status-label prs-status-void">ANULADA</span>
                {pick.result_note && (
                  <p className="prs-result-text prs-result-text--void">{pick.result_note}</p>
                )}
              </div>
            )}
          </div>
        )}

        <style>{`
          .pickcard-notes {
            margin-top: 12px; padding: 10px 12px; border-radius: 10px;
            background: linear-gradient(135deg, rgba(212,175,55,0.10), rgba(212,175,55,0.04));
            border: 1px solid rgba(212,175,55,0.32);
            animation: pickcard-notes-pulse 2.2s ease-in-out infinite;
          }
          .pickcard-notes-label {
            display: inline-flex; align-items: center; gap: 6px;
            font-size: 9.5px; font-weight: 900; letter-spacing: 0.18em;
            color: #D4AF37; margin-bottom: 5px; text-transform: uppercase;
          }
          .pickcard-notes-dot {
            width: 6px; height: 6px; border-radius: 999px; background: #D4AF37;
            box-shadow: 0 0 0 0 rgba(212,175,55,0.6);
            animation: pickcard-notes-dot 1.4s ease-in-out infinite;
          }
          .pickcard-notes-text {
            font-size: 12.5px; line-height: 1.5; color: #FAE8B0; font-weight: 600;
          }
          @keyframes pickcard-notes-pulse {
            0%, 100% { box-shadow: 0 0 0 0 rgba(212,175,55,0.0), 0 0 0 rgba(212,175,55,0); }
            50%      { box-shadow: 0 0 0 4px rgba(212,175,55,0.10), 0 0 16px rgba(212,175,55,0.18); }
          }
          @keyframes pickcard-notes-dot {
            0%, 100% { box-shadow: 0 0 0 0 rgba(212,175,55,0.7); }
            50%      { box-shadow: 0 0 0 5px rgba(212,175,55,0); }
          }
          .prs-won-block { display: flex; flex-direction: column; gap: 6px; }
          .prs-status-label { display: inline-flex; align-items: center; gap: 4px; font-size: 10px; font-weight: 800; letter-spacing: 0.15em; text-transform: uppercase; }
          .prs-status-won  { color: #10B981; }
          .prs-status-lost { color: #F43F5E; }
          .prs-status-void { color: #52525B; }
          .prs-check-icon { width: 11px; height: 11px; flex-shrink: 0; }
          .prs-result-text { font-size: 13px; font-weight: 700; line-height: 1.4; margin: 0; padding: 7px 12px; border-radius: 8px; }
          .prs-result-text--won  { color: #ecfdf5; background: rgba(16,185,129,0.08); border: 1.5px solid rgba(16,185,129,0.3); }
          .prs-result-text--lost { color: #ffe4e8; background: rgba(244,63,94,0.06); border: 1.5px solid rgba(244,63,94,0.2); }
          .prs-result-text--void { color: #71717A; background: rgba(255,255,255,0.03); border: 1.5px solid rgba(255,255,255,0.08); }
        `}</style>
      </div>
    </Link>
  );
}
